// React
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Libraries
import PropTypes from "prop-types";
import { Container, Icon, Button } from "semantic-ui-react";
import { filter, escapeRegExp, isEmpty, get, map } from "lodash";

// Redux
import { connect } from "react-redux";
import { chooseCompany } from "@Actions/authentication";
import genericAction from "@Actions/generic";

// Components
import Screen from "@Components/Screen";
import Input from "@Components/Input";
import Text from "@Components/Text";
import SectionSeparator from "./Components/SectionSeparator";
import InvitationCard from "./Components/InvitationCard";
import CompanyCard from "./Components/CompanyCard";

// Styles
import styles from "./styles.module.css";

const ChooseCompany = ({ dispatch, companyId }) => {
	const navigate = useNavigate();
	const [searchString, setSearchString] = useState("");
	const [allItems, setAllItems] = useState({});
	const [filteredItems, setFilteredItems] = useState({});

	useEffect(() => {
		(async () => {
			const companiesPromise = dispatch(genericAction({ method: "get", route: "/user/companies" }));
			const invitationsPromise = dispatch(genericAction({ method: "get", route: "/user/invitations" }));
			const [companiesResponse, invitationsResponse] = await Promise.all([companiesPromise, invitationsPromise]);
			const mergedResponses = {
				companies: [...companiesResponse],
				invitations: [...invitationsResponse],
			};
			setAllItems(mergedResponses);
			setFilteredItems(mergedResponses);
			if (companiesResponse.length === 1 && companyId === 0) {
				await dispatch(chooseCompany(companyId, companiesResponse[0].id, navigate));
			}
		})();
	}, []);

	const handleChooseCompany = async (item) => {
		await dispatch(chooseCompany(companyId, item.id, navigate));
	};

	const handleCancel = () => {
		navigate(-1, { replace: true });
	};

	const handleSearch = (e) => {
		const { value } = e.target;
		const regExp = new RegExp(escapeRegExp(value), "i");
		const isMatch = (item) => {
			return regExp.test(get(item, "name", "")) || regExp.test(get(item, "company.name", ""));
		};
		const filteredCompanies = filter(allItems.companies, isMatch);
		const filteredInvitations = filter(allItems.invitations, isMatch);
		setSearchString(value);
		setFilteredItems((prevState) => {
			return { ...prevState, companies: filteredCompanies, invitations: filteredInvitations };
		});
	};

	const renderInvitations = (array) => {
		if (isEmpty(array)) return null;
		const cards = map(array, (item) => {
			return <InvitationCard key={item.id} item={item} />;
		});
		return cards;
	};

	const renderCompanyCards = (array) => {
		if (isEmpty(array)) return null;

		const cards = map(array, (item) => {
			const isDisabled = !item.isActive || (companyId > 0 && companyId === item.id);
			const isCurrent = item.isActive && companyId === item.id;

			return (
				<CompanyCard
					key={item.id}
					item={item}
					isDisabled={isDisabled}
					isCurrent={isCurrent}
					onClick={() => {
						if (!isDisabled) handleChooseCompany(item);
					}}
				/>
			);
		});

		return cards;
	};

	return (
		<Screen hasHeader headerTitle="Compañía">
			<Container className={styles.container}>
				<div className={styles.headerSection}>
					<div className={styles.titleContainer}>
						<Text className={styles.titleText}>Seleccionar Compañía</Text>
						{companyId > 0 && (
							<Button negative onClick={handleCancel}>
								Cancelar
							</Button>
						)}
					</div>
					<Input
						rounded
						fluid
						icon="search"
						placeholder="Buscar por nombre"
						iconPosition="right"
						name="searchString"
						value={searchString}
						onChange={handleSearch}
					/>
				</div>
				<div className={styles.scrollableContent}>
					{!isEmpty(filteredItems.invitations) && (
						<>
							<SectionSeparator>
								<Text className={styles.sectionText}>Invitaciones</Text>
							</SectionSeparator>
							{renderInvitations(filteredItems.invitations)}
						</>
					)}
					{!isEmpty(filteredItems.companies) && (
						<>
							<SectionSeparator>
								<Text className={styles.sectionText}>Compañías</Text>
							</SectionSeparator>
							{renderCompanyCards(filteredItems.companies)}
						</>
					)}
					{isEmpty(filteredItems.companies) && (
						<div className={styles.emptyContainer}>
							<Icon className={styles.icon} name="frown outline" size="huge" />
							<Text className={styles.titleText}>No Estas Afiliado a Ninguna Compañía</Text>
							<Text className={styles.titleText}>Necesita activación por parte del administrador.</Text>
						</div>
					)}
				</div>
			</Container>
		</Screen>
	);
};

ChooseCompany.propTypes = {
	dispatch: PropTypes.func,
	companyId: PropTypes.number,
};

function mapStateToProps({ company }) {
	return {
		companyId: company.companyId,
	};
}

export default connect(mapStateToProps)(ChooseCompany);
